/**
 * BdrActivityLog — /log/bdr
 *
 * Self-reported daily activity log for BDR reps.
 * Sections: Phone Activity · Email/LinkedIn · Outcomes · Notes
 * Saves to localStorage via activity-log-store.ts
 *
 * Palette: #FAFAF7 bg · white cards · #0B0B0F black · #DC2626 red
 */

import { useState, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { useUnloadWarning } from "@/lib/useUnloadWarning";
import { BackBar } from "@/components/BackBar";
import { getSession } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import {
  saveBdrLog,
  getBdrLog,
  getBdrLogFromApi,
  emptyBdrFields,
  todayString,
  type BdrFields,
  type BdrLog,
} from "@/lib/activity-log-store";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

// ─── Numeric input ────────────────────────────────────────────────────────────

function NumInput({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  hint?: string;
}) {
  const filled = value > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-[10px] uppercase tracking-[0.16em] font-semibold leading-tight"
        style={{ color: "#6B6B70" }}
      >
        {label}
      </label>
      <input
        type="number"
        min={0}
        value={value === 0 ? "" : value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
        className="w-full text-lg font-semibold tabular-nums rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none transition-all bg-white"
        style={{
          color: filled ? MI6_BLACK : "#8E8E94",
        }}
        onFocus={(e) => {
          e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}33`;
          e.target.style.borderColor = MI6_RED;
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = "";
          e.target.style.borderColor = "";
        }}
      />
      {hint && (
        <span className="text-[10px] text-neutral-500 leading-tight">{hint}</span>
      )}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden"
      style={{ borderLeft: `4px solid ${MI6_BLACK}` }}
    >
      <div className="px-5 pt-4 pb-2 border-b border-neutral-100">
        <div
          className="text-[10px] uppercase tracking-[0.22em] font-bold"
          style={{ color: MI6_BLACK }}
        >
          {title}
        </div>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

interface BdrActivityLogProps {
  /** When rendered inside a modal, hide brand band/footer and provide onSave cb */
  modal?: boolean;
  onSaved?: () => void;
}

export default function BdrActivityLog({ modal = false, onSaved }: BdrActivityLogProps) {
  const { toast } = useToast();
  const session = getSession();
  const [date, setDate] = useState(todayString());
  const [fields, setFields] = useState<BdrFields>(emptyBdrFields());
  const [notes, setNotes] = useState("");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const userName = session?.name ?? "Rep";

  // Mark dirty when there is any input and the form is not yet saved.
  const hasFieldInput = Object.values(fields).some((v) => Number(v) > 0);
  const hasNotes = notes.trim().length > 0;
  useUnloadWarning(
    savedAt === null && (hasFieldInput || hasNotes),
    "You have unsaved BDR activity. Click Save before leaving."
  );

  // Load existing log when date changes.
  // Primary source: API (Supabase daily_activity_actual) — hydrates after hard refresh / new device.
  // Fallback: localStorage cache for pre-migration entries or offline mode.
  useEffect(() => {
    const uid = session?.id ?? "current_user";
    const userName = session?.name ?? "Rep";

    // Show cached data immediately (no flicker) then replace with API truth.
    const cached = getBdrLog(uid, date) ?? getBdrLog("current_user", date);
    if (cached) {
      setFields(cached.fields);
      setNotes(cached.notes);
      setSavedAt(cached.saved_at);
    } else {
      setFields(emptyBdrFields());
      setNotes("");
      setSavedAt(null);
    }

    // Then hydrate from server (source of truth post-migration).
    if (session?.id) {
      getBdrLogFromApi(uid, userName, date).then((apiLog) => {
        if (apiLog) {
          setFields(apiLog.fields);
          setNotes(apiLog.notes);
          // Format saved_at as a readable time string.
          const t = new Date(apiLog.saved_at);
          const timeStr = isNaN(t.getTime())
            ? apiLog.saved_at
            : t.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
          setSavedAt(timeStr);
        }
      }).catch(() => { /* API unreachable — cache already shown */ });
    }
  }, [date, session?.id]);

  function set(k: keyof BdrFields, v: number) {
    setFields((f) => ({ ...f, [k]: v }));
    setSavedAt(null); // Mark as dirty
  }

  async function handleSave() {
    // HARDENED (qa-fix-b): block save if session is not available.
    // A null session means the user is not authenticated or the session
    // hook has not resolved. Writing with a fallback string would either
    // fail at requireAuth middleware or corrupt local cache with a garbage user_id.
    if (!session?.id) {
      toast({
        title: "Session unavailable",
        description: "Your session could not be verified. Please reload the page and try again.",
        variant: "destructive",
      });
      return;
    }
    const now = new Date().toISOString();
    const userId = session.id;
    const log: BdrLog = {
      user_id: userId,
      user_name: userName,
      role: "bdr",
      date,
      fields,
      notes,
      saved_at: now,
    };

    // BUILD-13 Item 6: saveBdrLog now handles the Supabase write (all 12 fields)
    // AND the localStorage cache. No separate API call needed here.
    try {
      await saveBdrLog(log);

      const timeStr = new Date(now).toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
      setSavedAt(timeStr);
      onSaved?.();
    } catch (err: any) {
      // saveBdrLog catches internally — this path is for unexpected throws only.
      void saveBdrLog(log); // ensure local cache even on unexpected error
      const msg = err?.message ?? "Unknown error";
      console.error("[BdrActivityLog] Supabase write failed:", msg);
      toast({
        title: "Save failed",
        description: "Your activity was saved locally. It will sync when the connection is restored.",
        variant: "destructive",
      });
      setSavedAt("save failed — try again");
    }
  }

  function handleReset() {
    setFields(emptyBdrFields());
    setNotes("");
    setSavedAt(null);
    setDate(todayString());
  }

  const totalActivity =
    fields.dialsMade + fields.emailsSent + fields.linkedInDMs;

  const inner = (
    <div className="space-y-5">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div
        className="rounded-xl p-5 text-white"
        style={{ background: `linear-gradient(135deg, ${MI6_BLACK}, #1F2937)` }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">
              Mi6 Attilius · BDR
            </div>
            <h1 className="text-2xl font-bold">Daily Activity Log</h1>
            <div className="text-neutral-500 text-sm mt-0.5">
              Self-reported · manually entered
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-semibold">
              Log Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-neutral-600 bg-neutral-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
        {totalActivity > 0 && (
          <div className="mt-3 flex gap-4 text-sm">
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.dialsMade}</span> dials
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.emailsSent}</span> emails
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.meetingsBooked}</span> meetings booked
            </span>
          </div>
        )}
      </div>

      {/* ── Phone Activity ──────────────────────────────────────────────── */}
      <SectionCard title="Phone Activity">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
          <NumInput label="Dials Made" value={fields.dialsMade} onChange={(v) => set("dialsMade", v)} hint="Total outbound dials" />
          <NumInput label="Live Conversations" value={fields.liveConversations} onChange={(v) => set("liveConversations", v)} hint="Prospect picked up" />
          <NumInput label="Voicemails Left" value={fields.voicemailsLeft} onChange={(v) => set("voicemailsLeft", v)} hint="With a message" />
          <NumInput label="No-Message Hangups" value={fields.noMessageHangups} onChange={(v) => set("noMessageHangups", v)} hint="No voicemail left" />
        </div>
        {fields.dialsMade > 0 && (
          <div className="mt-3 pt-3 border-t border-neutral-100 flex gap-4 text-[11px] text-neutral-500">
            <span>
              Connect rate:{" "}
              <strong style={{ color: MI6_BLACK }}>
                {Math.round((fields.liveConversations / fields.dialsMade) * 100)}%
              </strong>
            </span>
            <span>
              VM rate:{" "}
              <strong style={{ color: MI6_BLACK }}>
                {Math.round((fields.voicemailsLeft / fields.dialsMade) * 100)}%
              </strong>
            </span>
          </div>
        )}
      </SectionCard>

      {/* ── Email / LinkedIn ────────────────────────────────────────────── */}
      <SectionCard title="Email / LinkedIn">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
          <NumInput label="Emails Sent" value={fields.emailsSent} onChange={(v) => set("emailsSent", v)} />
          <NumInput label="LinkedIn DMs" value={fields.linkedInDMs} onChange={(v) => set("linkedInDMs", v)} />
          <NumInput label="Email Replies Received" value={fields.emailRepliesReceived} onChange={(v) => set("emailRepliesReceived", v)} hint="Inbound responses" />
        </div>
        {fields.emailsSent > 0 && (
          <div className="mt-3 pt-3 border-t border-neutral-100 text-[11px] text-neutral-500">
            Reply rate:{" "}
            <strong style={{ color: MI6_BLACK }}>
              {Math.round((fields.emailRepliesReceived / fields.emailsSent) * 100)}%
            </strong>
          </div>
        )}
      </SectionCard>

      {/* ── Outcomes ────────────────────────────────────────────────────── */}
      <SectionCard title="Outcomes">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-3">
          <NumInput label="Suspects Qualified" value={fields.suspectsQualified} onChange={(v) => set("suspectsQualified", v)} hint="From calls today" />
          <NumInput label="Prospects Qualified" value={fields.prospectsQualified} onChange={(v) => set("prospectsQualified", v)} hint="Forwarded to AE" />
          <NumInput label="Meetings Booked" value={fields.meetingsBooked} onChange={(v) => set("meetingsBooked", v)} />
          <NumInput label="Meetings Held" value={fields.meetingsHeld} onChange={(v) => set("meetingsHeld", v)} hint="Confirmed happened" />
          <NumInput label="No-Shows" value={fields.noShows} onChange={(v) => set("noShows", v)} hint="Booked but no-showed" />
        </div>
        {fields.meetingsBooked > 0 && (
          <div className="mt-3 pt-3 border-t border-neutral-100 text-[11px] text-neutral-500">
            Show rate:{" "}
            <strong style={{ color: MI6_RED }}>
              {Math.round(((fields.meetingsBooked - fields.noShows) / fields.meetingsBooked) * 100)}%
            </strong>
          </div>
        )}
      </SectionCard>

      {/* ── Notes ───────────────────────────────────────────────────────── */}
      <SectionCard title="Notes">
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setSavedAt(null); }}
          placeholder="Anything notable from today — key conversations, objections heard, patterns noticed…"
          rows={4}
          className="w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm text-neutral-700 placeholder-neutral-300 focus:outline-none focus:border-transparent resize-none"
          onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}40`)}
          onBlur={(e) => (e.target.style.boxShadow = "")}
        />
        <div className="text-[10px] text-neutral-500 mt-1">Optional — visible to you only</div>
      </SectionCard>

      {/* ── Save bar ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 pb-4">
        <div className="flex items-center gap-3">
          {savedAt ? (
            <span className="text-sm font-semibold text-neutral-600">
              ✓ Saved at {savedAt}
            </span>
          ) : (hasFieldInput || hasNotes) ? (
            <span className="text-sm text-neutral-500">Unsaved changes</span>
          ) : (
            <span className="text-sm text-neutral-400">Ready to log activity</span>
          )}
          {savedAt && (
            <button
              onClick={handleReset}
              className="text-xs text-neutral-500 hover:text-neutral-600 underline underline-offset-2 transition-colors"
            >
              Clear &amp; restart
            </button>
          )}
        </div>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
          style={{ background: MI6_RED }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#B91C1C")}
          onMouseLeave={(e) => (e.currentTarget.style.background = MI6_RED)}
        >
          Save Today's Log
        </button>
      </div>
    </div>
  );

  if (modal) {
    return inner;
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "BDR Activity Log" }]} pageTitle="BDR Activity Log" pageMeta="Daily activity log">
      <BackBar href="/dashboards/bdr" label="BDR Dashboard" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">{inner}</main>
    </OperatorShell>
  );
}
